import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

export default function TopicDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [topic, setTopic] = useState(null);
  const [body, setBody] = useState("");

  const load = () => api.get(`/topics/${id}`).then((r) => setTopic(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const reply = async () => {
    if (!body.trim()) return;
    try {
      await api.post(`/topics/${id}/replies`, { body });
      setBody("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!topic) return <div className="max-w-3xl mx-auto px-5 py-16 text-[#4A5568] font-heading">Loading…</div>;

  const initials = (n) => (n ? n.slice(0, 2).toUpperCase() : "?");

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-12">
      <Link to="/forum" data-testid="back-to-forum" className="inline-flex items-center gap-2 text-[#4A5568] hover:text-[#1A202C] font-heading mb-6">
        <ArrowLeft size={18} /> Back to forum
      </Link>

      <div className="bg-white border border-[#6A0DAD] rounded-2xl p-7 shadow-sm">
        <span className="inline-block px-3 py-1 rounded-full bg-[#D4AF37]/12 text-[#B8972E] text-xs font-heading font-semibold mb-3">{topic.category}</span>
        <h1 className="font-display text-3xl md:text-4xl text-[#1A202C]">{topic.title}</h1>
        <div className="flex items-center gap-2 mt-3 text-sm text-[#4A5568] font-heading">
          <Avatar className="w-7 h-7"><AvatarFallback className="bg-[#6B8EAC] text-white text-xs">{initials(topic.author_name)}</AvatarFallback></Avatar>
          {topic.author_name} · {new Date(topic.created_at).toLocaleDateString()}
        </div>
        <p className="mt-5 text-[#1A202C] leading-relaxed whitespace-pre-wrap">{topic.body}</p>
      </div>

      <h2 className="font-heading text-lg font-semibold text-[#1A202C] mt-10 mb-4">
        {topic.replies.length} {topic.replies.length === 1 ? "reply" : "replies"}
      </h2>

      <div className="space-y-3">
        {topic.replies.map((r) => (
          <div key={r._id} data-testid={`reply-${r._id}`} className="bg-white border border-[#6A0DAD] rounded-2xl p-5 flex gap-3">
            <Avatar className="w-9 h-9 shrink-0"><AvatarFallback className="bg-[#6B8EAC] text-white text-sm">{initials(r.author_name)}</AvatarFallback></Avatar>
            <div>
              <p className="font-heading text-sm font-semibold text-[#1A202C]">{r.author_name} <span className="text-[#4A5568] font-normal">· {new Date(r.created_at).toLocaleDateString()}</span></p>
              <p className="text-[#1A202C] mt-1 leading-relaxed whitespace-pre-wrap">{r.body}</p>
            </div>
          </div>
        ))}
      </div>

      {user ? (
        <div className="mt-6 bg-white border border-[#6A0DAD] rounded-2xl p-4 flex gap-3 items-end">
          <textarea data-testid="reply-input" rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a kind reply…" className="flex-1 border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#6B8EAC] resize-none" />
          <button data-testid="reply-submit-btn" onClick={reply} className="grid place-items-center bg-[#6B8EAC] text-white hover:bg-[#5A7B96] rounded-full w-11 h-11 shrink-0 transition-colors">
            <Send size={18} />
          </button>
        </div>
      ) : (
        <Link to="/auth" className="mt-6 block text-center bg-white border border-[#6A0DAD] rounded-2xl py-4 text-[#6B8EAC] font-heading font-semibold">Sign in to reply</Link>
      )}
    </div>
  );
}
